import * as React from "react"
import { Eye, Pencil, Plus, X } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import {
  DISPLAY_CONFIGURATION_ITEM_OPTIONS,
  type DisplayConfigurationItem,
} from "@/types/alfred-config"

interface DisplayConfigurationEditorProps {
  items: DisplayConfigurationItem[]
  onChange: (next: DisplayConfigurationItem[]) => void
  disabled?: boolean
  triggerAriaLabel?: string
}

const LABEL_LOOKUP: Record<DisplayConfigurationItem, string> =
  DISPLAY_CONFIGURATION_ITEM_OPTIONS.reduce(
    (acc, opt) => {
      acc[opt.value] = opt.label
      return acc
    },
    {} as Record<DisplayConfigurationItem, string>,
  )

export function DisplayConfigurationEditor({
  items,
  onChange,
  disabled = false,
  triggerAriaLabel,
}: DisplayConfigurationEditorProps) {
  const [open, setOpen] = React.useState(false)
  // Local draft so the panel only sees changes when the user clicks Done.
  const [draft, setDraft] = React.useState<DisplayConfigurationItem[]>(items)

  React.useEffect(() => {
    if (open) setDraft(items)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const selectedSet = React.useMemo(() => new Set(draft), [draft])
  const available = DISPLAY_CONFIGURATION_ITEM_OPTIONS.filter(
    (opt) => !selectedSet.has(opt.value),
  )

  const remove = (index: number) => {
    setDraft(draft.filter((_, i) => i !== index))
  }

  const add = (value: DisplayConfigurationItem) => {
    if (selectedSet.has(value)) return
    setDraft([...draft, value])
  }

  const handleDone = () => {
    onChange(draft)
    setOpen(false)
  }

  const handleCancel = () => {
    setDraft(items)
    setOpen(false)
  }

  return (
    <>
      <Button
        aria-label={triggerAriaLabel}
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-card border-input text-foreground hover:bg-secondary"
      >
        {disabled ? <Eye className="h-4 w-4 mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
        {disabled ? "View" : "Manage"}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-[600px] sm:max-w-[600px] bg-card border-l border-border p-0 flex flex-col"
        >
          <SheetHeader className="px-6 py-5 border-b border-border">
            <SheetTitle>Display configuration</SheetTitle>
            <SheetDescription>
              Choose which configuration screens to show.
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            <section className="space-y-2">
              <h3 className="text-[13px] text-muted-foreground font-medium">
                Selected ({draft.length})
              </h3>
              {draft.length === 0 ? (
                <p className="text-sm text-muted-foreground italic border border-dashed border-border rounded-md p-4 text-center">
                  No screens selected. Add some from the list below.
                </p>
              ) : (
                <ol className="space-y-2">
                  {draft.map((value, index) => (
                    <li
                      key={value}
                      className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2"
                    >
                      <span className="text-xs text-muted-foreground tabular-nums w-6">
                        {index + 1}.
                      </span>
                      <span className="text-sm text-foreground flex-1">
                        {LABEL_LOOKUP[value] ?? value}
                      </span>
                      {!disabled && (
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => remove(index)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-secondary"
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Remove</span>
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
            {!disabled && (
              <section className="space-y-2">
                <h3 className="text-[13px] text-muted-foreground font-medium">
                  Available ({available.length})
                </h3>
                {available.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic border border-dashed border-border rounded-md p-4 text-center">
                    All screens have been added.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {available.map((opt) => (
                      <li
                        key={opt.value}
                        className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2"
                      >
                        <span className="text-sm text-foreground flex-1">{opt.label}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => add(opt.value)}
                          className="bg-card border-input text-foreground hover:bg-secondary"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
          <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
            {disabled ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Close
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleDone}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Done
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
